import type { Activity, ActivityRatingData } from '@website/services/activities.service';
import type { ReviewStatus } from '@website/services/reviews.service';

/**
 * Mirrors domain `updateReviewStatus` aggregate updates for optimistic UI.
 */
export function applyReviewStatusToActivityRating(
  activity: Activity,
  params: {
    previousStatus: ReviewStatus;
    newStatus: 'approved' | 'rejected';
    reviewRating: number;
  }
): Activity {
  const { previousStatus, newStatus, reviewRating } = params;
  const isBecomingApproved = newStatus === 'approved' && previousStatus !== 'approved';
  const isLeavingApproved = newStatus !== 'approved' && previousStatus === 'approved';

  if (!isBecomingApproved && !isLeavingApproved) {
    return activity;
  }

  const starIdx = Math.min(5, Math.max(1, Math.round(reviewRating))) - 1;
  const cur: ActivityRatingData | undefined = activity.rating;
  const ratingTotal = cur?.ratingTotal ?? 0;
  const ratingAverage = cur?.ratingAverage ?? 0;
  const dist: [number, number, number, number, number] = cur?.ratingDist ? [...cur.ratingDist] : [0, 0, 0, 0, 0];

  if (isBecomingApproved) {
    const newTotal = ratingTotal + 1;
    const newAvg = newTotal === 1 ? reviewRating : (ratingAverage * ratingTotal + reviewRating) / newTotal;
    const newDist = [...dist] as [number, number, number, number, number];
    newDist[starIdx] += 1;
    return {
      ...activity,
      rating: { ratingAverage: newAvg, ratingTotal: newTotal, ratingDist: newDist }
    };
  }

  const newTotal = Math.max(0, ratingTotal - 1);
  const newAvg = newTotal === 0 ? 0 : (ratingAverage * ratingTotal - reviewRating) / newTotal;
  const newDist = [...dist] as [number, number, number, number, number];
  newDist[starIdx] = Math.max(0, newDist[starIdx] - 1);

  return {
    ...activity,
    rating: newTotal > 0 ? { ratingAverage: newAvg, ratingTotal: newTotal, ratingDist: newDist } : undefined
  };
}
